import { useEffect, useRef, useState, type RefObject } from 'react';

export const PULL_TO_REFRESH_THRESHOLD = 64;
const DEFAULT_THRESHOLD = PULL_TO_REFRESH_THRESHOLD;
const MAX_PULL = 96;
const RESISTANCE = 0.45;

function isAtScrollTop(container: HTMLElement, target: EventTarget | null): boolean {
  if (container.scrollTop > 0) return false;

  let el = target instanceof Element ? target : null;
  while (el && el !== container) {
    const { overflowY } = window.getComputedStyle(el);
    const canScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    if (canScroll && el.scrollTop > 0) return false;
    el = el.parentElement;
  }

  return true;
}

export interface UsePullToRefreshOptions {
  containerRef: RefObject<HTMLElement | null>;
  onRefresh: () => void | Promise<void>;
  enabled?: boolean;
  threshold?: number;
}

export interface UsePullToRefreshResult {
  pullDistance: number;
  isRefreshing: boolean;
  isPulling: boolean;
}

export function usePullToRefresh({
  containerRef,
  onRefresh,
  enabled = true,
  threshold = DEFAULT_THRESHOLD,
}: UsePullToRefreshOptions): UsePullToRefreshResult {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const pullDistanceRef = useRef(0);
  const isRefreshingRef = useRef(false);
  const pullingRef = useRef(false);
  const startYRef = useRef(0);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enabled) {
      pullDistanceRef.current = 0;
      setPullDistance(0);
      pullingRef.current = false;
      return;
    }

    const setDistance = (value: number) => {
      pullDistanceRef.current = value;
      setPullDistance(value);
    };

    const onTouchStart = (event: TouchEvent) => {
      if (isRefreshingRef.current) return;
      if (!isAtScrollTop(container, event.target)) {
        pullingRef.current = false;
        return;
      }

      startYRef.current = event.touches[0]?.clientY ?? 0;
      pullingRef.current = true;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!pullingRef.current || isRefreshingRef.current) return;

      if (!isAtScrollTop(container, event.target) && pullDistanceRef.current === 0) {
        pullingRef.current = false;
        setDistance(0);
        return;
      }

      const currentY = event.touches[0]?.clientY ?? 0;
      const delta = currentY - startYRef.current;

      if (delta <= 0) {
        setDistance(0);
        return;
      }

      const distance = Math.min(delta * RESISTANCE, MAX_PULL);
      setDistance(distance);

      if (distance > 8) {
        event.preventDefault();
      }
    };

    const onTouchEnd = () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;

      const distance = pullDistanceRef.current;
      if (distance < threshold || isRefreshingRef.current) {
        setDistance(0);
        return;
      }

      setIsRefreshing(true);
      isRefreshingRef.current = true;
      setDistance(threshold);

      Promise.resolve(onRefreshRef.current())
        .catch(() => undefined)
        .finally(() => {
          setIsRefreshing(false);
          isRefreshingRef.current = false;
          setDistance(0);
        });
    };

    const onTouchCancel = () => {
      pullingRef.current = false;
      if (!isRefreshingRef.current) {
        setDistance(0);
      }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);
    container.addEventListener('touchcancel', onTouchCancel);

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [containerRef, enabled, threshold]);

  return {
    pullDistance,
    isRefreshing,
    isPulling: pullDistance > 0 || isRefreshing,
  };
}
